import { z } from "zod";
import { transitionSchema } from "./transition.js";
import { imageSizeSchema, previewUrlSchema, previewUrlWithSizeSchema, } from "./preview-url.js";
export const fontFamilySchema = z.union([
    z.literal("Futura"),
    z.literal("Poppins"),
    z.literal("Poiret One"),
    z.literal("Prata"),
    z.literal("Bree Serif"),
    z.literal("Parisienne"),
    z.literal("Cookie"),
    z.literal("Bangers"),
    z.literal("Permanent Marker"),
    z.literal("Architects Daughter"),
    z.literal("Cinzel Decorative"),
    z.literal("Fredericka the Great"),
    z.literal("Special Elite"),
    z.literal("Pinyon Script"),
    z.literal("EB Garamond"),
    z.literal("Dosis"),
    z.literal("Quattrocento"),
    z.literal("Besley"),
    z.literal("Satisfy"),
    z.literal("Libre Baskerville"),
    z.literal("Quicksand"),
    z.literal("Alex Brush"),
    z.literal("Kristi"),
    z.literal("Oswald"),
    z.literal("FrankRuhlLibre"),
    z.literal("Glass Antiqua"),
    z.literal("Londrina Solid"),
    z.literal("Advent Pro"),
    z.literal("Stint Ultra Condensed"),
    z.literal("Stint Ultra Expanded"),
    z.literal("Cormorant SC"),
    z.literal("Cutive Mono"),
    z.literal("Kalnia Thin"),
    z.literal("Berkshire Swash"),
]);
export const fontWeightSchema = z.union([
    z.literal("thin"),
    z.literal("extralight"),
    z.literal("light"),
    z.literal("normal"),
    z.literal("medium"),
    z.literal("semibold"),
    z.literal("bold"),
    z.literal("extrabold"),
    z.literal("black"),
]);
export const fontLetterSpacingSchema = z.union([
    z.literal("tighter"),
    z.literal("tight"),
    z.literal("normal"),
    z.literal("wide"),
    z.literal("wider"),
    z.literal("widest"),
]);
export const textTransformSchema = z.union([
    z.literal("none"),
    z.literal("capitalize"),
    z.literal("uppercase"),
    z.literal("lowercase"),
    z.literal("full-width"),
    z.literal("full-size-kana"),
]);
export const textAlignSchema = z.union([
    z.literal("left"),
    z.literal("right"),
    z.literal("center"),
]);
export const doodleAnimateSchema = z.object({
    // this is kind of a degree value, but more like how for we'll go between
    // each extreme of rotation
    rotate: z.number().optional(),
    // Max distance from the position. Could go in any direction, but won't go
    // further than this
    translate: z.object({ x: z.number(), y: z.number() }).optional(),
    scale: z.number().optional(),
});
export const clipTextThemeSchema = z.object({
    backgroundColor: z.string(),
    backgroundOpacity: z.number(),
    textColor: z.string(),
    fontFamily: fontFamilySchema,
    fontWeight: fontWeightSchema,
    fontSize: z.number(),
    nudge: z.number(),
    textTransform: textTransformSchema,
    custom: z.any().optional(),
});
const captionVariantCommonSchema = z.object({
    captionDurationSeconds: z.number().optional(),
    // Wait this long to display the caption. For most variants, this value will
    // be  added to the transition time. So don't add the transition time to this
    // value. However, for circle and slide-in-static this will be the delay from
    // the start of the scene, not from the transition. In that case, you might
    // want to add the transition time to this value if you want the caption to
    // start showing after the transition.
    startOffsetSeconds: z.number().optional(),
});
const captionDoodleImageSchema = z.object({
    url: previewUrlWithSizeSchema,
    // override the size of the image
    displaySize: imageSizeSchema.optional(),
    style: z.record(z.string(), z.any()),
    zPosition: z.union([z.literal("below"), z.literal("above")]).optional(),
    overflow: z.union([z.literal("hidden"), z.literal("visible")]).optional(),
});
const captionVariantBackgroundImageSchema = z.object({
    url: previewUrlSchema,
    style: z.record(z.string(), z.any()).optional(),
});
const captionVariantSchema = z.discriminatedUnion("variant", [
    captionVariantCommonSchema.extend({ variant: z.literal("default") }),
    captionVariantCommonSchema.extend({
        variant: z.literal("circle"),
        colors: z.array(z.string()),
    }),
    captionVariantCommonSchema.extend({
        variant: z.literal("image-stretch"),
        color: z.string().optional(),
        rightUrl: previewUrlSchema.optional(),
        leftUrl: previewUrlSchema.optional(),
        center: z.discriminatedUnion("variant", [
            z.object({
                // Stretch the image to fill the height of the area and then repeat it
                // on the x-axis.
                variant: z.literal("stretch-y-repeat-x"),
                url: previewUrlSchema,
            }),
            z.object({
                // Repeat the image on the x and y axes.
                variant: z.literal("repeat-x-y"),
                url: previewUrlSchema,
            }),
            z.object({
                // Stretch the image to fill the height and width of the area
                variant: z.literal("stretch-x-y"),
                url: previewUrlSchema,
            }),
        ]),
        backgroundUrl: previewUrlSchema.optional(),
        maxWidthPx: z.number(),
        textContainerStyleOverride: z.record(z.string(), z.any()).optional(),
        backgroundStyleOverride: z.record(z.string(), z.any()).optional(),
    }),
    captionVariantCommonSchema.extend({
        variant: z.literal("slide-in-static"),
        entranceVariant: z.enum([
            // slide out and end flush to the left of the screen
            "slide-to-flush-left",
            // slide+jiggle out and end a little offset from the left
            "jiggle-to-offset-left",
        ]),
        // If true, captions without a subtitle will be the same height as those
        // with subtitles. This is useful for themes that have doodles positioned
        // such that a shorter caption will look weird.
        preserveHeightForEmptySubtitles: z.boolean().optional(),
        transitionDurationSeconds: z.number().optional(),
        // will cycle through these styles for each clip
        cards: z
            .object({
            backgroundColor: z.string(),
            backgroundOpacity: z.number().optional(),
            doodleImages: captionDoodleImageSchema.array().optional(),
            backgroundImages: captionVariantBackgroundImageSchema
                .array()
                .optional(),
            paddingLeft: z.string().optional(),
            paddingRight: z.string().optional(),
            paddingTop: z.string().optional(),
            paddingBottom: z.string().optional(),
            borderRadius: z.string().optional(),
            borderWidth: z.string().optional(),
            borderColor: z.string().optional(),
        })
            .array(),
    }),
    captionVariantCommonSchema.extend({
        variant: z.literal("candles"),
        candleUrls: z.array(previewUrlSchema),
    }),
]);
const clipCornerOverlaySchema = z.object({
    variant: z.literal("corner"),
    url: z.string(),
    /** Any CSS value with units to offset the corners (e.g. "-1rem").
     * This will be set, as-is, on the top, right, left, and bottom CSS properties
     * for the respective corners. */
    offsetUnit: z.string(),
});
const doodleClipOverlaySchema = z.object({
    variant: z.literal("doodle"),
    images: captionDoodleImageSchema.array(),
    animate: doodleAnimateSchema.optional(),
});
const clipOverlaySchema = z.discriminatedUnion("variant", [
    clipCornerOverlaySchema,
    doodleClipOverlaySchema,
]);
const circleBackgroundSchema = z.object({
    variant: z.literal("circle"),
    colors: z.array(z.string()),
});
const imagePlacementSchema = z.object({
    url: previewUrlSchema,
    width: z.string(),
    height: z.string().optional(),
    transformations: z.enum(["flip-x"]).array().optional(),
    // above or below the clip
    zPosition: z.enum(["above", "below"]).optional(),
    top: z.number().optional(),
    left: z.number().optional(),
    right: z.number().optional(),
    bottom: z.number().optional(),
    rotateDeg: z.number().optional(),
});
const imageBackgroundSchema = z.object({
    variant: z.literal("image"),
    // Multiple images at the same time, but the images change based
    // on the index of the clip that we're currently viewing.
    images: z.array(imagePlacementSchema.array()),
});
const backgroundVariantSchema = z.discriminatedUnion("variant", [
    circleBackgroundSchema,
    imageBackgroundSchema,
]);
export const themeStylesSchema = z.object({
    textBox: z.object({
        backgroundColor: z.string(),
        backgroundOpacity: z.number(),
        backgroundBlur: z.number(),
        textAlign: textAlignSchema,
        textColor: z.string(),
        fontFamily: fontFamilySchema,
        fontWeight: fontWeightSchema,
        fontSize: z.number(),
        leading: z.number(),
        tracking: z.number(),
        backdropCustom: z.any().optional(),
        containerCustom: z.any().optional(),
        custom: z.any().optional(),
    }),
    clips: z.object({
        betweenTransition: transitionSchema,
        textVariant: captionVariantSchema,
        photoTextVariant: captionVariantSchema.optional(),
        videoTextVariant: captionVariantSchema.optional(),
        backgroundVariant: backgroundVariantSchema.optional(),
        title: clipTextThemeSchema,
        subtitle: clipTextThemeSchema,
        scaleFactor: z.number().optional(),
        scaleMode: z
            .union([
            z.literal("fill"),
            // Full-bleed, stretching the image to fill the entire frame,
            // and positioning it based on the aspect ratio of the video.
            // Landscapes are given a bit of bottom padding, portraits
            // are centered, and square-ish are aligned with the left or right.
            z.literal("aspect-bleed"),
        ])
            .optional(),
        overlay: clipOverlaySchema.optional(),
        borderRadius: z.string().optional(),
        boxShadow: z.string().optional(),
        border: z.string().optional(),
    }),
    watermark: z.object({
        opacity: z.number().gte(0).lte(1),
        margin: z.number(),
        enterTransition: transitionSchema,
        exitTransition: transitionSchema,
    }),
    sceneTransition: transitionSchema,
});
